import tlds from 'tlds';

import {
  GenericOrderReadAttributesSegmentationTypeEnum,
  GenericOrderReadAttributesStatusEnum,
  PaymentReadAttributesPaymentStatusEnum,
} from '@spazious/client-backoffice';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_FORMAT_DATE_PICKER = 'yyyy-MM-dd';
export const DATE_FORMAT_HANDY = 'MMM DD, YYYY';
export const DATE_FORMAT_MONTH_YEAR = 'MMM - YY';
export const TIME_FORMAT = 'HH:mm';
export const DATETIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
// Seconds precision, for inclusive end-of-window bounds that must not spill into the next day.
export const DATETIME_FORMAT_SECONDS = `${DATE_FORMAT} ${TIME_FORMAT}:ss`;
export const DATETIME_FORMAT_DATEPICKER = `${DATE_FORMAT_DATE_PICKER} ${TIME_FORMAT}`;

export const EMAIL_REGEX = new RegExp(
  `^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@(([a-zA-Z\\-0-9]+\\.)+(${tlds.join(
    '|'
  )}|co.uk))$`
);

export const DEBOUNCE_TIMEOUT = 300;

export const ALPHA_QUARTER_OPACITY = 40;

const ALLOWED_STATUSES = [
  'Tentative',
  'Potential',
  'Blocked',
  'Confirmed',
  'Closed',
  'Canceled',
  'Turndown',
];

export const ORDER_STATUSES = {
  [GenericOrderReadAttributesStatusEnum.DraftBackoffice]: 'Draft',
  [GenericOrderReadAttributesStatusEnum.Tentative]: 'Tentative',
  [GenericOrderReadAttributesStatusEnum.Option]: 'Option',
  [GenericOrderReadAttributesStatusEnum.Potential]: 'Potential',
  [GenericOrderReadAttributesStatusEnum.Blocked]: 'Blocked',
  [GenericOrderReadAttributesStatusEnum.Confirmed]: 'Confirmed',
  [GenericOrderReadAttributesStatusEnum.Canceled]: 'Canceled',
  [GenericOrderReadAttributesStatusEnum.Turndown]: 'Turndown',
  [GenericOrderReadAttributesStatusEnum.Closed]: 'Closed',
};

export const STATUS_OPTIONS = Object.entries(ORDER_STATUSES).map(([value, label]) => ({
  value,
  label,
}));

export const ORDER_STATUSES_WITHOUT_CANCELED = Object.keys(ORDER_STATUSES).filter(
  (status) => status !== GenericOrderReadAttributesStatusEnum.Canceled
);

export const STATUSES_COLOURS = {
  [GenericOrderReadAttributesStatusEnum.Blocked]: '#eabd00',
  [GenericOrderReadAttributesStatusEnum.Canceled]: '#b80000',
  [GenericOrderReadAttributesStatusEnum.Closed]: '#1f1355',
  [GenericOrderReadAttributesStatusEnum.Confirmed]: '#007a02',
  [GenericOrderReadAttributesStatusEnum.DraftBackoffice]: '#bfbfbf',
  [GenericOrderReadAttributesStatusEnum.Option]: '#007af1',
  [GenericOrderReadAttributesStatusEnum.Tentative]: '#BFD8FA',
  [GenericOrderReadAttributesStatusEnum.Turndown]: '#f00000',
  [GenericOrderReadAttributesStatusEnum.Potential]: '#e8c3a2',
};

export const STATUSES_SLUGS = {
  Tentative: GenericOrderReadAttributesStatusEnum.Tentative,
  Potential: GenericOrderReadAttributesStatusEnum.Potential,
  Blocked: GenericOrderReadAttributesStatusEnum.Blocked,
  Confirmed: GenericOrderReadAttributesStatusEnum.Confirmed,
  Closed: GenericOrderReadAttributesStatusEnum.Closed,
  Canceled: GenericOrderReadAttributesStatusEnum.Canceled,
  Turndown: GenericOrderReadAttributesStatusEnum.Turndown,
  DraftBackoffice: GenericOrderReadAttributesStatusEnum.DraftBackoffice,
  Option: GenericOrderReadAttributesStatusEnum.Option,
};

export const STATUS_MAP = Object.keys(ORDER_STATUSES).reduce((acc, key) => {
  const statusName = ORDER_STATUSES[key];
  if (ALLOWED_STATUSES.includes(statusName)) {
    acc[key] = {
      name: statusName,
      primaryColor: STATUSES_COLOURS[key],
      slug: STATUSES_SLUGS[statusName],
    };
  }
  return acc;
}, {});

// Statuses that require a cancel reason when set.
export const cancelReasonStatuses = [
  GenericOrderReadAttributesStatusEnum.Canceled,
  GenericOrderReadAttributesStatusEnum.Turndown,
];

export const SIZE_PER_PAGE_LIST = [
  { text: '10', value: 10 },
  { text: '25', value: 25 },
  { text: '50', value: 50 },
];

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const EVENT_TYPES_MAPPING = {
  [GenericOrderReadAttributesSegmentationTypeEnum.Congress]: 'Congress',
  [GenericOrderReadAttributesSegmentationTypeEnum.GroupTravel]: 'Group Travel',
  [GenericOrderReadAttributesSegmentationTypeEnum.Incentives]: 'Incentives',
  [GenericOrderReadAttributesSegmentationTypeEnum.MeetingOrConference]: 'Meeting or Conference',
  [GenericOrderReadAttributesSegmentationTypeEnum.SocialEvent]: 'Social Event',
  [GenericOrderReadAttributesSegmentationTypeEnum.Wedding]: 'Wedding',
};

export const SEGMENTATION_TYPES_OPTIONS = Object.values(
  GenericOrderReadAttributesSegmentationTypeEnum
).map((value) => ({
  label: EVENT_TYPES_MAPPING[value] || '',
  value: value,
}));

export const PAYMENT_ACTION_MAPPING = {
  [PaymentReadAttributesPaymentStatusEnum.Confirmed]: 'Confirm',
  [PaymentReadAttributesPaymentStatusEnum.Pending]: 'Unconfirm',
  [PaymentReadAttributesPaymentStatusEnum.Canceled]: 'Cancel',
};

export const ORDER_ACTIVITY = 'OrderActivity';

export const MEETING_ROOM_BLOCK = 'MeetingRoomBlock';

export const DATE_RANGE = {
  Year: 'year',
  Month: 'month',
  Day: 'day',
};

export const DATE_SWITCHER = {
  Created: 'created_at',
  Starts: 'start_date',
};

export const ORDER_KPIS = {
  Orders: 'total_count',
  Amount: 'total_amount',
};

export const LEADS_KPIS = {
  Orders: 'total_count',
};

export const DATE_DIAGRAMMING_SWITCHER = {
  Created: 'created_at',
  Starts: 'activity__order__start_date',
};

export const PAGINATION_KEYS = new Set(['page', 'pageSize']);
